import { useState, useEffect } from 'react';
import { Check, ChevronDown, Users } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';

export interface DashboardFilterOption {
  id: string;
  name: string;
  initials?: string;
  type: 'all' | 'user' | 'team';
  avatarColor?: string;
}

interface DashboardFilterProps {
  selectedFilter: DashboardFilterOption;
  onFilterChange: (filter: DashboardFilterOption) => void;
}

const filterOptions: DashboardFilterOption[] = [
  { id: 'everyone', name: 'Everyone', type: 'all' },
  { id: 'user-001', name: 'Sarah Johnson', initials: 'SJ', type: 'user', avatarColor: '#3B82F6' },
  { id: 'user-002', name: 'Mike Chen', initials: 'MC', type: 'user', avatarColor: '#8B5CF6' },
  { id: 'user-003', name: 'Emily Rodriguez', initials: 'ER', type: 'user', avatarColor: '#EC4899' },
  { id: 'user-004', name: 'David Kim', initials: 'DK', type: 'user', avatarColor: '#F59E0B' }
];

export function DashboardFilter({ selectedFilter, onFilterChange }: DashboardFilterProps) {
  const [open, setOpen] = useState(false);

  // Persist filter to localStorage
  useEffect(() => {
    if (selectedFilter.id !== 'everyone') {
      localStorage.setItem('dashboardFilter', JSON.stringify(selectedFilter));
    } else {
      localStorage.removeItem('dashboardFilter');
    }
  }, [selectedFilter]);

  const handleFilterSelect = (filter: DashboardFilterOption) => {
    onFilterChange(filter);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2">
          {selectedFilter.id === 'everyone' ? (
            <Users className="h-4 w-4" />
          ) : (
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
              style={{ backgroundColor: selectedFilter.avatarColor }}
            >
              {selectedFilter.initials}
            </div>
          )}
          <span>{selectedFilter.name}</span>
          <ChevronDown className="h-4 w-4 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        {filterOptions.map((option, index) => (
          <div key={option.id}>
            {index === 1 && <DropdownMenuSeparator />}
            <DropdownMenuItem
              onClick={() => handleFilterSelect(option)}
              className="cursor-pointer"
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                  {option.id === 'everyone' ? (
                    <Users className="h-4 w-4" />
                  ) : (
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-semibold"
                      style={{ backgroundColor: option.avatarColor }}
                    >
                      {option.initials}
                    </div>
                  )}
                  <span>{option.name}</span>
                </div>
                {selectedFilter.id === option.id && (
                  <Check className="h-4 w-4" />
                )}
              </div>
            </DropdownMenuItem>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
